import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Phone, ArrowRight, Trash2, Home, Archive, Car, Heart, Wrench, User, Leaf, Zap, Check, CircleDollarSign, MapPin, Clock, Star, CheckCircle } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { companyInfo, services, benefits, idealFor, onRequest, images } from '../data/mock';

const iconMap = {
  Trash2: Trash2,
  Home: Home,
  Archive: Archive,
  Car: Car,
  Heart: Heart,
  Wrench: Wrench,
  User: User,
  BadgeSwissFranc: CircleDollarSign,
  Leaf: Leaf,
  Zap: Zap
};

const HomePage = () => {
  useEffect(() => {
    // Reset to main SEO title when returning to homepage
    document.title = 'Entrümpelung & Räumungen Deutschschweiz | Haushaltsauflösung Zürich, Bern, Luzern | Helper-Schweiz';
    const metaDesc = document.querySelector('meta[name="description"]');
    if (metaDesc) {
      metaDesc.setAttribute('content', '★ Entrümpelung & Räumungen in der ganzen Deutschschweiz ★ Haushaltsauflösung, Wohnungsräumung, Kellerräumung in Zürich, Bern, Luzern, St. Gallen, Thurgau, Aargau ✓ Faire Preise ✓ Schnell & Sauber ☎ +41 77 252 38 20');
    }
  }, []);

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-[#f0f5fa] to-[#e8f0f8] py-16 md:py-24">
        <div className="max-w-7xl mx-auto px-4">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-[#1e3a5f] leading-tight">
                Räumungen, Entrümpelung & Haushaltsauflösungen in der ganzen Deutschschweiz
              </h1>
              <p className="mt-6 text-lg text-gray-600 leading-relaxed">
                Ihr zuverlässiger Partner für professionelles Entrümpeln, Räumen und Entsorgen. Ich bin in der <strong>gesamten Deutschschweiz</strong> für Sie unterwegs – von Zürich über Bern bis Chur. Schnell, sauber und fair.
              </p>
              <div className="mt-6 flex flex-wrap gap-4">
                <div className="flex items-center gap-2 text-gray-700">
                  <MapPin className="w-5 h-5 text-[#c41e3a]" />
                  <span>Deutschschweiz</span>
                </div>
                <div className="flex items-center gap-2 text-gray-700">
                  <Clock className="w-5 h-5 text-[#c41e3a]" />
                  <span>Schnelle Termine</span>
                </div>
                <div className="flex items-center gap-2 text-gray-700">
                  <Star className="w-5 h-5 text-[#c41e3a]" />
                  <span>Faire Preise</span>
                </div>
              </div>
              <div className="mt-8 flex flex-wrap gap-4">
                <Link to="/contact">
                  <Button className="bg-[#c41e3a] hover:bg-[#a31830] text-white px-8 py-3 rounded-full text-lg flex items-center gap-2">
                    Kostenlos anfragen
                    <ArrowRight className="w-5 h-5" />
                  </Button>
                </Link>
                <a href={`tel:${companyInfo.phone}`}>
                  <Button variant="outline" className="border-2 border-[#1e3a5f] text-[#1e3a5f] hover:bg-[#1e3a5f] hover:text-white px-8 py-3 rounded-full text-lg flex items-center gap-2">
                    <Phone className="w-5 h-5" />
                    Jetzt anrufen
                  </Button>
                </a>
              </div>
            </div>
            <div className="relative">
              <div className="rounded-2xl overflow-hidden shadow-2xl">
                <img
                  src={companyInfo.vehicleImage}
                  alt="Helper-Schweiz Entrümpelung Fahrzeug Deutschschweiz"
                  className="w-full h-auto"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Einzugsgebiet Section - für lokale SEO */}
      <section className="py-12 bg-white border-b">
        <div className="max-w-7xl mx-auto px-4">
          <div className="text-center">
            <h2 className="text-2xl font-bold text-[#1e3a5f] mb-4">Entrümpelung & Räumungen in der ganzen Deutschschweiz</h2>
            <p className="text-gray-600 mb-4">Ich bin in der <strong>gesamten Deutschschweiz</strong> für Sie unterwegs!</p>
            <p className="text-gray-600 mb-4">Hauptgebiet: <strong>Kanton Thurgau & Bodensee-Region</strong></p>
            <div className="flex flex-wrap justify-center gap-2 mb-6">
              {['Scherzingen', 'Kreuzlingen', 'Frauenfeld', 'Arbon', 'Weinfelden', 'Amriswil', 'Romanshorn', 'Münsterlingen', 'Ermatingen', 'Steckborn'].map((city) => (
                <span key={city} className="bg-[#1e3a5f] text-white px-4 py-2 rounded-full text-sm font-medium">
                  {city}
                </span>
              ))}
            </div>
            <p className="text-gray-600 mb-4"><strong>Ganze Deutschschweiz</strong> – von Zürich bis Graubünden:</p>
            <div className="flex flex-wrap justify-center gap-2 mb-4">
              {['Zürich', 'Winterthur', 'St. Gallen', 'Rorschach', 'Wil SG', 'Rapperswil-Jona', 'Schaffhausen', 'Baden', 'Aarau', 'Luzern', 'Zug', 'Chur'].map((region) => (
                <span key={region} className="bg-[#f0f5fa] text-[#1e3a5f] px-4 py-2 rounded-full text-sm font-medium">
                  {region}
                </span>
              ))}
            </div>
            <div className="flex flex-wrap justify-center gap-2">
              {['Bern', 'Solothurn', 'Basel', 'Olten', 'Thun', 'Biel', 'Burgdorf', 'Langenthal', 'Appenzell', 'Herisau', 'Glarus', 'Schwyz'].map((region) => (
                <span key={region} className="bg-[#f0f5fa] text-[#1e3a5f] px-4 py-2 rounded-full text-sm font-medium">
                  {region}
                </span>
              ))}
            </div>
            <p className="text-gray-500 text-sm mt-6">Entrümpelung, Haushaltsauflösung, Wohnungsräumung, Kellerräumung, Nachlassauflösung – überall in der Deutschschweiz</p>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section className="py-20 bg-white" id="dienstleistungen">
        <div className="max-w-7xl mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-[#1e3a5f]">Räumungen, Entrümpeln & Entsorgen im Thurgau</h2>
            <p className="mt-4 text-gray-600 text-lg">Professionelle Räumungen, Entsorgung und Haushaltsauflösungen aus einer Hand</p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {services.map((service) => {
              const IconComponent = iconMap[service.icon];
              return (
                <Card key={service.id} className="group hover:shadow-lg transition-all duration-300 border-0 shadow-md">
                  <CardContent className="p-6">
                    <div className="w-14 h-14 bg-[#f0f5fa] rounded-xl flex items-center justify-center mb-4 group-hover:bg-[#c41e3a] transition-colors">
                      {IconComponent && <IconComponent className="w-7 h-7 text-[#1e3a5f] group-hover:text-white transition-colors" />}
                    </div>
                    <h3 className="text-xl font-semibold text-[#1e3a5f] mb-2">{service.title}</h3>
                    <p className="text-gray-600">{service.description}</p>
                  </CardContent>
                </Card>
              );
            })}
          </div>
          <div className="text-center mt-10">
            <Link to="/services">
              <Button variant="outline" className="border-2 border-[#1e3a5f] text-[#1e3a5f] hover:bg-[#1e3a5f] hover:text-white px-8 py-3 rounded-full">
                Alle Dienstleistungen ansehen
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-20 bg-[#f8fafc]">
        <div className="max-w-7xl mx-auto px-4">
          <h2 className="text-3xl md:text-4xl font-bold text-[#1e3a5f] text-center mb-12">Warum Helper-Schweiz?</h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {benefits.map((benefit) => {
              const IconComponent = iconMap[benefit.icon];
              return (
                <div key={benefit.id} className="text-center">
                  <div className="w-16 h-16 bg-white rounded-full flex items-center justify-center mx-auto mb-4 shadow-md">
                    {IconComponent && <IconComponent className="w-8 h-8 text-[#c41e3a]" />}
                  </div>
                  <h3 className="text-lg font-semibold text-[#1e3a5f] mb-2">{benefit.title}</h3>
                  <p className="text-gray-600 text-sm">{benefit.description}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* What I Can Do Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl md:text-4xl font-bold text-[#1e3a5f] mb-8">Was ich für Sie tun kann</h2>
              <div className="grid md:grid-cols-2 gap-8">
                <div>
                  <h3 className="text-xl font-semibold text-[#1e3a5f] mb-4">Ideal geeignet:</h3>
                  <ul className="space-y-3">
                    {idealFor.map((item, index) => (
                      <li key={index} className="flex items-start gap-2">
                        <Check className="w-5 h-5 text-[#c41e3a] mt-0.5 flex-shrink-0" />
                        <span className="text-gray-600">{item}</span>
                      </li>
                    ))}
                  </ul>
                </div>
                <div>
                  <h3 className="text-xl font-semibold text-[#1e3a5f] mb-4">Auf Anfrage:</h3>
                  <ul className="space-y-3">
                    {onRequest.map((item, index) => (
                      <li key={index} className="flex items-start gap-2">
                        <Check className="w-5 h-5 text-[#1e3a5f] mt-0.5 flex-shrink-0" />
                        <span className="text-gray-600">{item}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
              <p className="mt-8 text-gray-600">
                Kontaktieren Sie mich für eine unverbindliche Besprechung Ihres Projekts.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section - für Google "People Also Ask" */}
      <section className="py-20 bg-white">
        <div className="max-w-4xl mx-auto px-4">
          <h2 className="text-3xl md:text-4xl font-bold text-[#1e3a5f] text-center mb-4">Häufige Fragen zur Entrümpelung</h2>
          <p className="text-gray-600 text-center mb-12">Antworten auf die wichtigsten Fragen rund um Entrümpelung und Haushaltsauflösung</p>
          
          <div className="space-y-4">
            <details className="bg-[#f8fafc] rounded-xl p-6 group">
              <summary className="font-semibold text-[#1e3a5f] cursor-pointer flex justify-between items-center">
                Was kostet eine Entrümpelung in der Schweiz?
                <span className="text-[#c41e3a] group-open:rotate-180 transition-transform">▼</span>
              </summary>
              <p className="mt-4 text-gray-600">
                Die Kosten für eine Entrümpelung hängen von verschiedenen Faktoren ab: Grösse des Objekts, Menge des Räumguts, Zugänglichkeit und Entsorgungsaufwand. Ich biete transparente Kostenvoranschläge und faire Preise. Bei verwertbaren Gegenständen ist eine Wertanrechnung möglich. Kontaktieren Sie mich für ein unverbindliches Angebot.
              </p>
            </details>

            <details className="bg-[#f8fafc] rounded-xl p-6 group">
              <summary className="font-semibold text-[#1e3a5f] cursor-pointer flex justify-between items-center">
                Wie lange dauert eine Haushaltsauflösung?
                <span className="text-[#c41e3a] group-open:rotate-180 transition-transform">▼</span>
              </summary>
              <p className="mt-4 text-gray-600">
                Eine kleine Wohnung (1-2 Zimmer) kann oft an einem Tag geräumt werden. Grössere Objekte oder umfangreiche Haushaltsauflösungen können 2-3 Tage dauern. Die genaue Dauer bespreche ich mit Ihnen im Vorfeld, damit Sie planen können.
              </p>
            </details>

            <details className="bg-[#f8fafc] rounded-xl p-6 group">
              <summary className="font-semibold text-[#1e3a5f] cursor-pointer flex justify-between items-center">
                Werden die Gegenstände umweltgerecht entsorgt?
                <span className="text-[#c41e3a] group-open:rotate-180 transition-transform">▼</span>
              </summary>
              <p className="mt-4 text-gray-600">
                Ja, ich lege grossen Wert auf umweltgerechte Entsorgung. Verwertbare Materialien werden recycelt, Sondermüll fachgerecht entsorgt und noch brauchbare Gegenstände werden nach Möglichkeit gespendet oder weiterverwendet.
              </p>
            </details>

            <details className="bg-[#f8fafc] rounded-xl p-6 group">
              <summary className="font-semibold text-[#1e3a5f] cursor-pointer flex justify-between items-center">
                Räumen Sie auch nach einem Todesfall?
                <span className="text-[#c41e3a] group-open:rotate-180 transition-transform">▼</span>
              </summary>
              <p className="mt-4 text-gray-600">
                Ja, ich biete einfühlsame Nachlassauflösungen an. Mit Respekt und Diskretion kümmere ich mich um die Räumung und Auflösung nach einem Todesfall. Wertgegenstände und persönliche Erinnerungsstücke werden selbstverständlich an die Angehörigen übergeben.
              </p>
            </details>

            <details className="bg-[#f8fafc] rounded-xl p-6 group">
              <summary className="font-semibold text-[#1e3a5f] cursor-pointer flex justify-between items-center">
                In welchen Regionen sind Sie tätig?
                <span className="text-[#c41e3a] group-open:rotate-180 transition-transform">▼</span>
              </summary>
              <p className="mt-4 text-gray-600">
                Ich bin in der <strong>gesamten Deutschschweiz</strong> unterwegs! Von Schaffhausen bis Chur, von Basel bis St. Gallen – überall wo Sie mich brauchen. Ob Zürich, Bern, Winterthur, Luzern, Aarau, Baden, Thun, Solothurn, Zug oder kleinere Gemeinden – ich komme zu Ihnen. Mein Hauptgebiet ist der Kanton Thurgau und die Bodensee-Region, aber für Sie fahre ich gerne in die ganze Deutschschweiz.
              </p>
            </details>

            <details className="bg-[#f8fafc] rounded-xl p-6 group">
              <summary className="font-semibold text-[#1e3a5f] cursor-pointer flex justify-between items-center">
                Muss ich bei der Entrümpelung dabei sein?
                <span className="text-[#c41e3a] group-open:rotate-180 transition-transform">▼</span>
              </summary>
              <p className="mt-4 text-gray-600">
                Nein, das ist nicht zwingend notwendig. Nach einer gemeinsamen Begehung und klaren Absprachen kann die Entrümpelung auch ohne Ihre Anwesenheit durchgeführt werden. Ich arbeite zuverlässig und vertrauenswürdig.
              </p>
            </details>

            <details className="bg-[#f8fafc] rounded-xl p-6 group">
              <summary className="font-semibold text-[#1e3a5f] cursor-pointer flex justify-between items-center">
                Bieten Sie auch besenreine Übergabe an?
                <span className="text-[#c41e3a] group-open:rotate-180 transition-transform">▼</span>
              </summary>
              <p className="mt-4 text-gray-600">
                Ja, auf Wunsch hinterlasse ich das Objekt besenrein. So ist die Wohnung oder das Haus direkt für die Übergabe an Vermieter oder neue Eigentümer bereit. Diese Leistung kann optional hinzugebucht werden.
              </p>
            </details>
          </div>
        </div>
      </section>

      {/* Aktuelle Aufträge / Galerie Section */}
      <section className="py-24 bg-gradient-to-b from-white to-[#f8fafc]">
        <div className="max-w-7xl mx-auto px-4">
          <div className="text-center mb-16">
            <span className="inline-block px-4 py-1 bg-[#1e3a5f]/10 text-[#1e3a5f] rounded-full text-sm font-medium mb-4">
              REFERENZEN
            </span>
            <h2 className="text-3xl md:text-4xl font-bold text-[#1e3a5f]">Einblicke in meine Arbeit</h2>
            <p className="mt-4 text-gray-600 text-lg max-w-2xl mx-auto">
              Professionelle Räumungen und Entrümpelungen in der ganzen Deutschschweiz – hier einige Beispiele meiner Aufträge.
            </p>
          </div>
          
          {/* Zürich Auftrag */}
          <div className="mb-16">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-12 h-12 bg-[#1e3a5f] rounded-full flex items-center justify-center">
                <MapPin className="w-6 h-6 text-white" />
              </div>
              <div>
                <h3 className="text-2xl font-bold text-[#1e3a5f]">Kontaktlose Aufträge</h3>
                <p className="text-[#c41e3a] font-medium">Zürich</p>
              </div>
            </div>
            <p className="text-gray-600 mb-8 ml-16">
              Einfach Gegenstände bereitstellen – ich hole ab, ohne dass Sie vor Ort sein müssen. Schnell, diskret und unkompliziert.
            </p>
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-5">
              <div className="group relative rounded-2xl overflow-hidden shadow-xl bg-white">
                <div className="aspect-[4/3] overflow-hidden">
                  <img 
                    src="https://customer-assets.emergentagent.com/job_399e0e1d-0742-4a28-be18-df8dc62f42a6/artifacts/vice5fcr_IMG_20260131_105642_975.jpg"
                    alt="Kontaktloser Auftrag Zürich - Gegenstände bereitgestellt"
                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                  />
                </div>
                <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                <div className="absolute bottom-0 left-0 right-0 p-5 translate-y-full group-hover:translate-y-0 transition-transform duration-300">
                  <p className="text-white font-medium text-sm">Bereitstellung zur Abholung</p>
                </div>
                <div className="absolute top-3 right-3 bg-white/95 backdrop-blur-sm px-3 py-1 rounded-full">
                  <span className="text-[#1e3a5f] text-xs font-semibold">ZÜRICH</span>
                </div>
              </div>
              <div className="group relative rounded-2xl overflow-hidden shadow-xl bg-white">
                <div className="aspect-[4/3] overflow-hidden">
                  <img 
                    src="https://customer-assets.emergentagent.com/job_399e0e1d-0742-4a28-be18-df8dc62f42a6/artifacts/ynzsmw4k_IMG_20260221_092458.jpg"
                    alt="Kontaktloser Auftrag Zürich - Matratze im Transporter"
                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                  />
                </div>
                <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                <div className="absolute bottom-0 left-0 right-0 p-5 translate-y-full group-hover:translate-y-0 transition-transform duration-300">
                  <p className="text-white font-medium text-sm">Sicher verladen</p>
                </div>
                <div className="absolute top-3 right-3 bg-white/95 backdrop-blur-sm px-3 py-1 rounded-full">
                  <span className="text-[#1e3a5f] text-xs font-semibold">ZÜRICH</span>
                </div>
              </div>
              <div className="group relative rounded-2xl overflow-hidden shadow-xl bg-white">
                <div className="aspect-[4/3] overflow-hidden">
                  <img 
                    src="https://customer-assets.emergentagent.com/job_399e0e1d-0742-4a28-be18-df8dc62f42a6/artifacts/1mfbgtgf_IMG_20260221_091837~2.jpg"
                    alt="Matratzenentsorgung Zürich - Abholung vor Ort"
                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                  />
                </div>
                <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                <div className="absolute bottom-0 left-0 right-0 p-5 translate-y-full group-hover:translate-y-0 transition-transform duration-300">
                  <p className="text-white font-medium text-sm">Abholung vor Ort</p>
                </div>
                <div className="absolute top-3 right-3 bg-white/95 backdrop-blur-sm px-3 py-1 rounded-full">
                  <span className="text-[#1e3a5f] text-xs font-semibold">ZÜRICH</span>
                </div>
              </div>
              <div className="group relative rounded-2xl overflow-hidden shadow-xl bg-white">
                <div className="aspect-[4/3] overflow-hidden">
                  <img 
                    src="https://customer-assets.emergentagent.com/job_399e0e1d-0742-4a28-be18-df8dc62f42a6/artifacts/rn5o0z7c_IMG_20260221_092502.jpg"
                    alt="Helper-Schweiz Transporter vor Gebäude Zürich"
                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                  />
                </div>
                <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                <div className="absolute bottom-0 left-0 right-0 p-5 translate-y-full group-hover:translate-y-0 transition-transform duration-300">
                  <p className="text-white font-medium text-sm">Schnell & zuverlässig</p>
                </div>
                <div className="absolute top-3 right-3 bg-white/95 backdrop-blur-sm px-3 py-1 rounded-full">
                  <span className="text-[#1e3a5f] text-xs font-semibold">ZÜRICH</span>
                </div>
              </div>
            </div>
          </div>
          
          {/* Grössere Räumungen */}
          <div className="mb-16">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-12 h-12 bg-[#1e3a5f] rounded-full flex items-center justify-center">
                <Trash2 className="w-6 h-6 text-white" />
              </div>
              <div>
                <h3 className="text-2xl font-bold text-[#1e3a5f]">Grössere Räumungen & Haushaltsauflösungen</h3>
                <p className="text-[#c41e3a] font-medium">Deutschschweiz</p>
              </div>
            </div>
            <p className="text-gray-600 mb-8 ml-16">
              Von einem einzelnen Gegenstand bis hin zur kompletten Haushaltsauflösung – kein Auftrag ist zu klein oder zu gross. Ich kümmere mich um alles.
            </p>
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-5">
              <div className="group relative rounded-2xl overflow-hidden shadow-xl bg-white">
                <div className="aspect-[4/3] overflow-hidden">
                  <img 
                    src="https://customer-assets.emergentagent.com/job_399e0e1d-0742-4a28-be18-df8dc62f42a6/artifacts/2nn39ftc_1.jpeg"
                    alt="Büroräumung Schweiz - Möbel und Kartons im Transporter"
                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                  />
                </div>
                <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                <div className="absolute bottom-0 left-0 right-0 p-5 translate-y-full group-hover:translate-y-0 transition-transform duration-300">
                  <p className="text-white font-medium text-sm">Büro- & Geschäftsräumung</p>
                </div>
                <div className="absolute top-3 right-3 bg-[#1e3a5f] px-3 py-1 rounded-full">
                  <span className="text-white text-xs font-semibold">RÄUMUNG</span>
                </div>
              </div>
              <div className="group relative rounded-2xl overflow-hidden shadow-xl bg-white">
                <div className="aspect-[4/3] overflow-hidden">
                  <img 
                    src="https://customer-assets.emergentagent.com/job_399e0e1d-0742-4a28-be18-df8dc62f42a6/artifacts/f9x8by28_WhatsApp%20Image%202026-05-27%20at%2023.53.15.jpeg"
                    alt="Haushaltsauflösung Schweiz - Voller Transporter mit Möbeln"
                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                  />
                </div>
                <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                <div className="absolute bottom-0 left-0 right-0 p-5 translate-y-full group-hover:translate-y-0 transition-transform duration-300">
                  <p className="text-white font-medium text-sm">Komplette Haushaltsauflösung</p>
                </div>
                <div className="absolute top-3 right-3 bg-[#1e3a5f] px-3 py-1 rounded-full">
                  <span className="text-white text-xs font-semibold">HAUSHALT</span>
                </div>
              </div>
              <div className="group relative rounded-2xl overflow-hidden shadow-xl bg-white">
                <div className="aspect-[4/3] overflow-hidden">
                  <img 
                    src="https://customer-assets.emergentagent.com/job_399e0e1d-0742-4a28-be18-df8dc62f42a6/artifacts/878gb9vf_WhatsApp%20Image%202026-05-27%20at%2023.52.02.jpeg"
                    alt="Wohnungsräumung Schweiz - Transporter voll beladen"
                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                  />
                </div>
                <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                <div className="absolute bottom-0 left-0 right-0 p-5 translate-y-full group-hover:translate-y-0 transition-transform duration-300">
                  <p className="text-white font-medium text-sm">Wohnungsräumung</p>
                </div>
                <div className="absolute top-3 right-3 bg-[#1e3a5f] px-3 py-1 rounded-full">
                  <span className="text-white text-xs font-semibold">WOHNUNG</span>
                </div>
              </div>
              <div className="group relative rounded-2xl overflow-hidden shadow-xl bg-white">
                <div className="aspect-[4/3] overflow-hidden">
                  <img 
                    src="https://customer-assets.emergentagent.com/job_399e0e1d-0742-4a28-be18-df8dc62f42a6/artifacts/senmow2p_WhatsApp%20Image%202026-05-27%20at%2023.58.48.jpeg"
                    alt="Kellerräumung Schweiz - Voller Keller mit Kartons"
                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                  />
                </div>
                <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                <div className="absolute bottom-0 left-0 right-0 p-5 translate-y-full group-hover:translate-y-0 transition-transform duration-300">
                  <p className="text-white font-medium text-sm">Keller- & Estrichräumung</p>
                </div>
                <div className="absolute top-3 right-3 bg-[#1e3a5f] px-3 py-1 rounded-full">
                  <span className="text-white text-xs font-semibold">KELLER</span>
                </div>
              </div>
            </div>
          </div>
          
          {/* Luzern & Thalwil Row */}
          <div className="grid lg:grid-cols-2 gap-12 mb-16">
            {/* Luzern Auftrag */}
            <div>
              <div className="flex items-center gap-3 mb-6">
                <div className="w-10 h-10 bg-[#1e3a5f] rounded-full flex items-center justify-center">
                  <MapPin className="w-5 h-5 text-white" />
                </div>
                <div>
                  <h3 className="text-xl font-bold text-[#1e3a5f]">Kellerräumung</h3>
                  <p className="text-[#c41e3a] font-medium text-sm">Luzern</p>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="group relative rounded-2xl overflow-hidden shadow-xl">
                  <div className="aspect-square overflow-hidden">
                    <img 
                      src="https://customer-assets.emergentagent.com/job_399e0e1d-0742-4a28-be18-df8dc62f42a6/artifacts/qa11pavu_IMG_20260201_170719_558.jpg"
                      alt="Kellerräumung Luzern - voller Keller vor der Räumung"
                      className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                    />
                  </div>
                  <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                  <div className="absolute top-3 left-3 bg-[#c41e3a] px-3 py-1 rounded-full">
                    <span className="text-white text-xs font-semibold">VORHER</span>
                  </div>
                </div>
                <div className="group relative rounded-2xl overflow-hidden shadow-xl">
                  <div className="aspect-square overflow-hidden">
                    <img 
                      src="https://customer-assets.emergentagent.com/job_399e0e1d-0742-4a28-be18-df8dc62f42a6/artifacts/vbyruz4n_IMG_20260131_141818_892.jpg"
                      alt="Kellerräumung Luzern - Transporter voll beladen"
                      className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                    />
                  </div>
                  <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                  <div className="absolute top-3 left-3 bg-green-600 px-3 py-1 rounded-full">
                    <span className="text-white text-xs font-semibold">VERLADEN</span>
                  </div>
                </div>
              </div>
            </div>
            
            {/* Thalwil Auftrag */}
            <div>
              <div className="flex items-center gap-3 mb-6">
                <div className="w-10 h-10 bg-[#1e3a5f] rounded-full flex items-center justify-center">
                  <MapPin className="w-5 h-5 text-white" />
                </div>
                <div>
                  <h3 className="text-xl font-bold text-[#1e3a5f]">Gartenräumung</h3>
                  <p className="text-[#c41e3a] font-medium text-sm">Thalwil</p>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="group relative rounded-2xl overflow-hidden shadow-xl">
                  <div className="aspect-square overflow-hidden">
                    <img 
                      src="https://customer-assets.emergentagent.com/job_399e0e1d-0742-4a28-be18-df8dc62f42a6/artifacts/8hjb6doy_Screenshot_20260309-213536.png"
                      alt="Kontaktloser Auftrag Thalwil - Gegenstände bereitgestellt"
                      className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                    />
                  </div>
                  <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                  <div className="absolute top-3 left-3 bg-[#c41e3a] px-3 py-1 rounded-full">
                    <span className="text-white text-xs font-semibold">VORHER</span>
                  </div>
                </div>
                <div className="group relative rounded-2xl overflow-hidden shadow-xl">
                  <div className="aspect-square overflow-hidden">
                    <img 
                      src="https://customer-assets.emergentagent.com/job_399e0e1d-0742-4a28-be18-df8dc62f42a6/artifacts/hd6g01h6_Screenshot_20260309-213523.png"
                      alt="Entrümpelung Thalwil - Transporter beladen"
                      className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                    />
                  </div>
                  <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                  <div className="absolute top-3 left-3 bg-green-600 px-3 py-1 rounded-full">
                    <span className="text-white text-xs font-semibold">VERLADEN</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
          
          {/* Messie Wohnungen - Spezieller Bereich */}
          <div className="bg-[#1e3a5f] rounded-3xl p-8 md:p-12">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-12 h-12 bg-white/20 rounded-full flex items-center justify-center">
                <MapPin className="w-6 h-6 text-white" />
              </div>
              <div>
                <h3 className="text-2xl font-bold text-white">Messie-Wohnung Entrümpelung</h3>
                <p className="text-blue-200">Diskret & Einfühlsam</p>
              </div>
            </div>
            <p className="text-blue-100 mb-8 max-w-2xl">
              Professionelle und verständnisvolle Räumung – ohne Vorurteile, mit Respekt und absoluter Diskretion.
            </p>
            <div className="grid md:grid-cols-2 gap-6">
              <div className="group relative rounded-2xl overflow-hidden shadow-2xl">
                <div className="aspect-video overflow-hidden">
                  <img 
                    src="https://customer-assets.emergentagent.com/job_399e0e1d-0742-4a28-be18-df8dc62f42a6/artifacts/qmt6yzef_Screenshot_20260309-213438.png"
                    alt="Messie Wohnung Entrümpelung Schweiz"
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                  />
                </div>
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
                <div className="absolute bottom-4 left-4 right-4">
                  <span className="bg-white/20 backdrop-blur-sm text-white text-xs px-3 py-1 rounded-full">
                    Ausgangssituation
                  </span>
                </div>
              </div>
              <div className="group relative rounded-2xl overflow-hidden shadow-2xl">
                <div className="aspect-video overflow-hidden">
                  <img 
                    src="https://customer-assets.emergentagent.com/job_399e0e1d-0742-4a28-be18-df8dc62f42a6/artifacts/ndm7twe3_Screenshot_20260309-213451.png"
                    alt="Messie Entrümpelung Deutschschweiz"
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                  />
                </div>
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
                <div className="absolute bottom-4 left-4 right-4">
                  <span className="bg-white/20 backdrop-blur-sm text-white text-xs px-3 py-1 rounded-full">
                    Ich helfe – diskret & professionell
                  </span>
                </div>
              </div>
            </div>
          </div>
          
          <div className="text-center mt-12">
            <div className="inline-flex items-center gap-2 bg-[#f0f5fa] px-6 py-3 rounded-full">
              <CheckCircle className="w-5 h-5 text-green-600" />
              <span className="text-[#1e3a5f] font-medium">Kontaktlose Aufträge möglich – einfach bereitstellen, ich hole ab!</span>
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials / Trust Section */}
      <section className="py-16 bg-[#f8fafc]">
        <div className="max-w-7xl mx-auto px-4">
          <h2 className="text-3xl font-bold text-[#1e3a5f] text-center mb-12">Das zeichnet mich aus</h2>
          <div className="grid md:grid-cols-4 gap-6">
            <div className="bg-white rounded-xl p-6 text-center shadow-sm">
              <div className="text-4xl font-bold text-[#c41e3a] mb-2">100%</div>
              <p className="text-gray-600">Persönlicher Service</p>
            </div>
            <div className="bg-white rounded-xl p-6 text-center shadow-sm">
              <div className="text-4xl font-bold text-[#c41e3a] mb-2">24h</div>
              <p className="text-gray-600">Schnelle Antwort</p>
            </div>
            <div className="bg-white rounded-xl p-6 text-center shadow-sm">
              <div className="text-4xl font-bold text-[#c41e3a] mb-2">♻️</div>
              <p className="text-gray-600">Umweltgerecht</p>
            </div>
            <div className="bg-white rounded-xl p-6 text-center shadow-sm">
              <div className="text-4xl font-bold text-[#c41e3a] mb-2">CHF</div>
              <p className="text-gray-600">Faire Preise</p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-[#1e3a5f]">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
            Entrümpelung, Räumung oder Entsorgung gesucht?
          </h2>
          <p className="text-blue-100 text-lg mb-8">
            Kontaktieren Sie mich noch heute für ein kostenloses und unverbindliches Angebot
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <Link to="/contact">
              <Button className="bg-[#c41e3a] hover:bg-[#a31830] text-white px-8 py-3 rounded-full text-lg">
                Kostenlose Anfrage
              </Button>
            </Link>
            <a href={`tel:${companyInfo.phone}`}>
              <Button variant="outline" className="border-2 border-white text-white hover:bg-white hover:text-[#1e3a5f] px-8 py-3 rounded-full text-lg">
                {companyInfo.phone}
              </Button>
            </a>
          </div>
          <p className="text-blue-200 text-sm mt-6">
            Räumungen • Entrümpelung • Haushaltsauflösungen • Entsorgen • Wohnungsräumung • Kellerräumung • Nachlassauflösung • Räumung Schweiz
          </p>
        </div>
      </section>
    </div>
  );
};

export default HomePage;
